import { useEffect, useRef, useState, type CSSProperties } from 'react';
import ProductivityComic, { comicPanels, panelDuration, preloadPanel } from './ProductivityComic';
import '../../styles/ComicReel.scss';

const LAST_PANEL = comicPanels.length - 1;

/** Plays the story once and stops on the last panel, where the call to action is. */
export default function ComicReel({ onPanelChange }: { onPanelChange?: (panel: number) => void }) {
    const [panel, setPanel] = useState(0);
    const [paused, setPaused] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const [visible, setVisible] = useState(false);
    const root = useRef<HTMLElement>(null);
    const ended = paused && panel === LAST_PANEL;

    useEffect(() => {
        const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => setReducedMotion(preference.matches);
        preference.addEventListener('change', update);
        const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: .25 });
        if (root.current) observer.observe(root.current);
        return () => { preference.removeEventListener('change', update); observer.disconnect(); };
    }, []);

    // Only a panel change notifies the page: a new callback identity must not re-sync the showcase.
    const panelListener = useRef(onPanelChange);
    panelListener.current = onPanelChange;
    useEffect(() => { panelListener.current?.(panel); }, [panel]);

    // Once the reel is on screen, fetch the next panel while the current one plays.
    useEffect(() => {
        if (visible) preloadPanel((panel + 1) % comicPanels.length);
    }, [panel, visible]);

    useEffect(() => {
        if (!visible || paused || reducedMotion) return;
        const timer = window.setTimeout(() => {
            if (panel === LAST_PANEL) setPaused(true);
            else setPanel(panel + 1);
        }, panelDuration(panel));
        return () => window.clearTimeout(timer);
    }, [panel, paused, reducedMotion, visible]);

    const select = (next: number) => { setPanel((next + comicPanels.length) % comicPanels.length); setPaused(true); };
    const togglePlayback = () => {
        if (ended) setPanel(0);
        setPaused(value => !value);
    };
    const playbackLabel = ended ? 'Riguarda il fumetto dall’inizio' : paused ? 'Riprendi il fumetto animato' : 'Metti in pausa il fumetto animato';

    return <section ref={root} className={`sp-comic-reel${paused || reducedMotion ? ' is-paused' : ''}`} aria-label="Il fumetto animato di Luca e Sara" style={{ '--sp-comic-duration': `${panelDuration(panel)}ms` } as CSSProperties}>
        <div className="sp-comic-reel__header"><span>LUCA &amp; SARA / SEMPLORA STORIES</span><span>{String(panel + 1).padStart(2, '0')} / {String(comicPanels.length).padStart(2, '0')}</span></div>
        <ProductivityComic panel={panel} />
        <div className="sp-comic-reel__controls">
            <button type="button" aria-label="Vignetta precedente" onClick={() => select(panel - 1)}>←</button>
            <div role="group" aria-label="Scegli una vignetta">{comicPanels.map((_, index) => <button type="button" key={index} aria-label={`Vignetta ${index + 1} di ${comicPanels.length}`} aria-pressed={index === panel} onClick={() => select(index)}><span /></button>)}</div>
            <button type="button" aria-label="Vignetta successiva" onClick={() => select(panel + 1)}>→</button>
            {!reducedMotion && <button type="button" className="sp-comic-reel__pause" onClick={togglePlayback} aria-label={playbackLabel}>{ended ? '↺' : paused ? '▶' : 'Ⅱ'}</button>}
        </div>
    </section>;
}
